import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { SeverityLevel, appInsights } from '../config/appInsights';
import { telemetryService } from 'api/telemetryService';
import { sanitizeUrl, sanitizeParams } from 'utils/sanitizeUrl';

// Create axios instance with basic configuration
const axiosServices = axios.create({
  baseURL: import.meta.env.VITE_APP_API_URL || 'https://localhost:3000/',
  timeout: 30000
});

interface CustomInternalAxiosRequestConfig extends InternalAxiosRequestConfig {
  scope?: string;
}

interface RequestTiming {
  startTime: number;
  url: string;
  method: string;
}

export interface ApiError<TData = unknown> {
  status: number;
  data: TData | undefined;
  lockedBy: string | null;
  message: string;
  title?: string;
  detail?: string;
}

const requestTimings = new Map<string, RequestTiming>();

const isRecord = (value: unknown): value is Record<string, any> => value !== null && typeof value === 'object';

const getStringProperty = (data: unknown, ...keys: string[]): string | undefined => {
  if (!isRecord(data)) {
    return undefined;
  }

  const value = keys.map((key) => data[key]).find((property) => typeof property === 'string' && property.trim().length > 0);

  return typeof value === 'string' ? value : undefined;
};

export function normalizeApiError<TData = unknown>(error: AxiosError<TData>): ApiError<TData> {
  const data = error.response?.data;
  const status = error.response?.status ?? 0;
  const title = getStringProperty(data, 'title', 'Title');
  const detail = getStringProperty(data, 'detail', 'Detail');
  const lockedBy = getStringProperty(data, 'lockedBy', 'LockedBy') ?? null;
  const message = title ?? detail ?? (typeof data === 'string' ? data : undefined) ?? error.message ?? 'API request failed';

  return {
    status,
    data,
    lockedBy,
    message,
    ...(title ? { title } : {}),
    ...(detail ? { detail } : {})
  };
}

function generateRequestId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

function buildFullUrlForLogging(config: InternalAxiosRequestConfig): string {
  let url = config.url || '';

  if (url.startsWith('http://') || url.startsWith('https://')) {
  } else {
    const baseURL = config.baseURL || '';
    url = baseURL + url;
  }

  // If there are params, create a sanitized copy and append them
  if (config.params && Object.keys(config.params).length > 0) {
    const sanitizedParams = sanitizeParams(config.params);
    const searchParams = new URLSearchParams();
    Object.entries(sanitizedParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => searchParams.append(key, String(v)));
      } else {
        searchParams.append(key, String(value));
      }
    });
    const separator = url.includes('?') ? '&' : '?';
    url += separator + searchParams.toString();
  }

  return url;
}

function getPathFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.pathname;
  } catch {
    const match = url.match(/https?:\/\/[^/]+(\/[^?]*)/);
    return match ? match[1] : url;
  }
}

/**
 * Categorize errors for better analysis
 */
function categorizeError(error: AxiosError): string {
  if (!error.response) {
    if (error.code === 'ECONNABORTED') return 'Timeout';
    if (error.code === 'ERR_NETWORK') return 'Network';
    return 'ConnectionError';
  }

  const status = error.response.status;

  if (status >= 400 && status < 500) {
    if (status === 401) return 'Unauthorized';
    if (status === 403) return 'Forbidden';
    if (status === 404) return 'NotFound';
    if (status === 409) return 'Conflict';
    if (status === 422) return 'ValidationError';
    return 'ClientError';
  }

  if (status >= 500) {
    return 'ServerError';
  }

  return 'UnknownError';
}

export const setUpAxiosInterceptors = (acquireAccessToken: (scope: string) => Promise<string>, logout: () => void) => {
  const requestInterceptor = axiosServices.interceptors.request.use(
    async (config: CustomInternalAxiosRequestConfig) => {
      const requestId = generateRequestId();
      config.headers.set('X-Request-ID', requestId);

      if (appInsights && appInsights.context?.telemetryTrace) {
        const traceContext = appInsights.context.telemetryTrace;
        if (traceContext.traceID && traceContext.parentID) {
          config.headers.set('traceparent', `00-${traceContext.traceID}-${traceContext.parentID}-01`);
          config.headers.set('Request-Id', `|${traceContext.traceID}.${traceContext.parentID}.`);
        }
      }

      const safeUrlForLogging = sanitizeUrl(buildFullUrlForLogging(config));

      // Store the sanitized URL for telemetry tracking
      requestTimings.set(requestId, {
        startTime: Date.now(),
        url: safeUrlForLogging,
        method: config.method?.toUpperCase() || 'GET'
      });

      const scope = config.scope;
      if (scope) {
        try {
          const accessToken = await acquireAccessToken(scope);
          if (accessToken) {
            config.headers['Authorization'] = `Bearer ${accessToken}`;
          }
        } catch (tokenError) {
          telemetryService.trackException(tokenError as Error, SeverityLevel.Warning, {
            operation: 'acquireAccessToken',
            scope,
            url: safeUrlForLogging
          });
          await logout();
        }
      }

      if (import.meta.env.MODE === 'development') {
        telemetryService.trackTrace(`API Request: ${config.method?.toUpperCase()} ${safeUrlForLogging}`, SeverityLevel.Verbose, {
          requestId,
          method: config.method,
          url: safeUrlForLogging
        });
      }

      return config;
    },
    (error) => {
      console.error('Pre-API Error:', error);
      telemetryService.trackException(error as Error, SeverityLevel.Error, {
        phase: 'request',
        message: 'Request configuration failed'
      });
      return Promise.reject(error);
    }
  );

  const responseInterceptor = axiosServices.interceptors.response.use(
    (response: AxiosResponse) => {
      const requestId = response.config.headers.get('X-Request-ID') as string;
      const timing = requestTimings.get(requestId);

      if (timing) {
        const duration = Date.now() - timing.startTime;
        const pathName = getPathFromUrl(timing.url);

        telemetryService.trackDependency(requestId, timing.method, timing.url, pathName, duration, true, response.status, {
          requestId,
          contentType: response.headers['content-type'],
          responseSize: response.headers['content-length']
        });

        telemetryService.trackApiPerformance(pathName, timing.method, duration, response.status, {
          requestId,
          success: true
        });

        // Clean up timing information
        requestTimings.delete(requestId);

        if (import.meta.env.MODE === 'development') {
          console.log(`API Success: ${timing.method} ${pathName} (${duration}ms)`);
        }
      }

      return response;
    },
    (error: AxiosError) => {
      console.error('Post-API Error:', error);

      // if it's a 409 handle this separately to trigger the pop-up
      const config = error.config as CustomInternalAxiosRequestConfig | undefined;
      const safeUrl = config ? sanitizeUrl(buildFullUrlForLogging(config)) : 'unknown';
      const requestId = error.config?.headers?.get('X-Request-ID') as string;
      const timing = requestId ? requestTimings.get(requestId) : null;

      if (timing) {
        const duration = Date.now() - timing.startTime;
        const pathName = getPathFromUrl(timing.url);
        const statusCode = error.response?.status || 0;

        telemetryService.trackDependency(requestId, timing.method, timing.url, pathName, duration, false, statusCode, {
          requestId,
          errorMessage: error.message,
          errorCode: error.code
        });

        telemetryService.trackException(error as Error, SeverityLevel.Error, {
          requestId,
          method: timing.method,
          url: timing.url,
          pathName,
          statusCode,
          duration,
          errorCode: error.code,
          responseData: JSON.stringify(error.response?.data || {})
        });

        telemetryService.trackApiPerformance(pathName, timing.method, duration, statusCode, {
          requestId,
          success: false,
          errorCode: error.code
        });

        requestTimings.delete(requestId);

        const errorCategory = categorizeError(error);
        telemetryService.trackEvent('API.Error', {
          category: errorCategory,
          method: timing.method,
          pathName,
          statusCode,
          duration,
          requestId
        });
      } else {
        if (config) {
          telemetryService.trackException(error as Error, SeverityLevel.Error, {
            message: 'API request failed without timing information',
            errorCode: error.code,
            statusCode: error.response?.status || 0,
            url: safeUrl
          });
        }
      }

      const apiError = normalizeApiError(error);

      if (apiError.status === 409) {
        telemetryService.trackEvent('API.LockConflict', {
          url: safeUrl,
          method: config?.method,
          lockedBy: apiError.lockedBy
        });
      } else if (apiError.status === 412) {
        const responseData = isRecord(apiError.data) ? apiError.data : {};

        telemetryService.trackEvent('API.ConcurrencyConflict', {
          url: safeUrl,
          method: config?.method,
          title: apiError.title,
          detail: apiError.detail,
          // if your backend includes them as extensions later:
          expectedVersion: responseData?.ExpectedVersion ?? responseData?.expectedVersion,
          currentVersion: responseData?.CurrentVersion ?? responseData?.currentVersion
        });
      }

      return Promise.reject(apiError);
    }
  );

  telemetryService.trackTrace('Axios interceptors initialized with Application Insights', SeverityLevel.Information);
  return { requestInterceptor, responseInterceptor };
};

export const removeAxiosInterceptors = ({
  requestInterceptor,
  responseInterceptor
}: {
  requestInterceptor: number;
  responseInterceptor: number;
}) => {
  axiosServices.interceptors.request.eject(requestInterceptor);
  axiosServices.interceptors.response.eject(responseInterceptor);

  // Track interceptors removal
  telemetryService.trackTrace('Axios interceptors removed', SeverityLevel.Information);
};

// Configure axios to handle arrays for .NET
axiosServices.defaults.paramsSerializer = (params) => {
  return new URLSearchParams(
    Object.entries(params).flatMap(([key, value]) => (Array.isArray(value) ? value.map((v) => [key, v]) : [[key, value]]))
  ).toString();
};

export default axiosServices;
